// Compact horizontal progress rail for an engagement's workflow stage — shows every tenant-defined
// stage in order (done / current / upcoming) so a non-technical user can see at a glance where an
// engagement sits, without reading raw stage codes. Stage list comes from the tenant's own workflow
// customization (backend-driven — never a hardcoded stage list, see web-app/CLAUDE.md).
import React from 'react';
import { cn } from '@/utils/cn';
import type { StageDef } from '@/redux/internal-services/workflowEngineApi';

export interface StageRailProps {
  stages: StageDef[];
  currentStageCode: string;
  className?: string;
}

export const StageRail: React.FC<StageRailProps> = ({ stages, currentStageCode, className }) => {
  const currentIndex = stages.findIndex((s) => s.stage_code === currentStageCode);

  return (
    <div className={cn('flex items-start', className)}>
      {stages.map((stage, i) => {
        const done = currentIndex >= 0 && i < currentIndex;
        const current = i === currentIndex;
        const last = i === stages.length - 1;
        return (
          <div key={stage.stage_code} className={cn('flex items-center', !last && 'flex-1')}>
            <div className="flex flex-col items-center">
              <span
                className={cn(
                  'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                  done && 'bg-success text-white',
                  current && 'h-6 w-6 bg-accent-fill text-on-accent ring-2 ring-ui-ring/30',
                  !done && !current && 'bg-ui-subtle text-ui-text3',
                )}
                title={stage.name}
              >
                {done ? '✓' : i + 1}
              </span>
              <span
                className={cn(
                  'mt-1 whitespace-nowrap text-center text-xs leading-tight',
                  current ? 'font-semibold text-ui-text' : 'text-ui-text3',
                )}
                title={stage.name}
              >
                {stage.name}
              </span>
            </div>
            {!last && (
              <div className={cn('mx-0.5 h-0.5 flex-1 rounded', done ? 'bg-success' : 'bg-ui-border')} />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default StageRail;
